/**
 * Create Bare-Metal Order Page
 * Multi-step form for custom bare-metal server configuration
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ServerIcon,
  CpuChipIcon,
  CircleStackIcon,
  MapPinIcon,
  ClockIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  KeyIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  UserIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/layout/Header';
import { baremetalApi, BaremetalOrderRequest, DiskLayout } from '../../services/baremetalApi';
import { sshKeyService } from '../../services/api';
import {
  BaremetalSpec,
  STORAGE_TYPES,
  GPU_OPTIONS,
  LOCATIONS,
  OS_OPTIONS,
  DURATION_OPTIONS,
} from '../../types/baremetal';

const CreateOrder: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Get project ID from localStorage
  const selectedProjectId = localStorage.getItem('selectedProjectId') || '';

  // Form state
  const [spec, setSpec] = useState<BaremetalSpec>({
    cpu: 8,
    ram: 32,
    storage: 500,
    storage_type: 'SSD',
    gpu: '',
    gpu_count: 0,
    location: 'Mumbai',
    os: 'Ubuntu 22.04',
    duration: 1,
  });

  const [notes, setNotes] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  // New fields
  const [serverName, setServerName] = useState('');
  const [sshKeyId, setSshKeyId] = useState('');
  const [bandwidthTier, setBandwidthTier] = useState<'standard' | 'premium' | 'enterprise'>('standard');
  const [backupEnabled, setBackupEnabled] = useState(false);
  const [monitoringEnabled, setMonitoringEnabled] = useState(false);
  const [technicalContactName, setTechnicalContactName] = useState('');
  const [technicalContactEmail, setTechnicalContactEmail] = useState('');
  const [technicalContactPhone, setTechnicalContactPhone] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Advanced configuration
  const [contractType, setContractType] = useState<'on_demand' | 'reserved'>('on_demand');
  const [commitmentPeriod, setCommitmentPeriod] = useState(0); // 0 for on-demand, 3/6/12 for reserved
  const [autoRenew, setAutoRenew] = useState(false);
  const [diskLayouts, setDiskLayouts] = useState<DiskLayout[]>([
    { disk_type: 'NVME', disk_count: 1, size_per_disk_gb: 500, layout: 'lvm', format: 'ext4', mountpoint: '/' },
  ]);
  const [cloudInitConfig, setCloudInitConfig] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Fetch SSH keys
  const { data: sshKeysData } = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => sshKeyService.list().then((res) => res.data),
  });

  const sshKeys = sshKeysData?.keys || [];

  // Fetch pricing from backend
  const { data: pricingData, isLoading: isPricingLoading } = useQuery({
    queryKey: [
      'baremetal-pricing',
      spec,
      contractType,
      commitmentPeriod,
      backupEnabled,
      monitoringEnabled,
      selectedProjectId,
    ],
    queryFn: () => {
      const pricingRequest: BaremetalOrderRequest = {
        project_id: selectedProjectId,
        ...spec,
        server_name: serverName || 'temp',
        bandwidth_tier: bandwidthTier,
        backup_enabled: backupEnabled,
        monitoring_enabled: monitoringEnabled,
        contract_type: contractType,
        commitment_period: contractType === 'reserved' ? commitmentPeriod : undefined,
      };
      return baremetalApi.calculatePricing(pricingRequest);
    },
    enabled: !!selectedProjectId && spec.cpu > 0 && spec.ram > 0,
    staleTime: 30000, // Cache for 30 seconds
  });

  // Use backend pricing or default to 0
  const backendPricing = pricingData?.data;
  const pricing = backendPricing
    ? {
        // Backend returns discounted price, calculate base price for display
        monthly_price_inr:
          backendPricing.commitment_discount > 0
            ? backendPricing.monthly_price_inr / (1 - backendPricing.commitment_discount)
            : backendPricing.monthly_price_inr,
        discounted_monthly_price_inr: backendPricing.monthly_price_inr,
        total_price_inr: backendPricing.total_price_inr,
        discount_percentage: backendPricing.commitment_discount * 100,
      }
    : {
        monthly_price_inr: 0,
        discounted_monthly_price_inr: 0,
        total_price_inr: 0,
        discount_percentage: 0,
      };

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: (data: BaremetalOrderRequest) => baremetalApi.placeOrder(data),
    onSuccess: (response) => {
      toast.success('Order created successfully!');
      navigate(`/portal/baremetal/checkout/${response.data.order_id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create order');
    },
  });

  const handleSubmit = () => {
    if (!termsAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    if (!selectedProjectId) {
      toast.error('Please select a project');
      return;
    }

    if (!serverName.trim()) {
      toast.error('Please provide a server name');
      return;
    }

    const orderRequest: BaremetalOrderRequest = {
      project_id: selectedProjectId,
      ...spec,
      notes: notes || undefined,
      server_name: serverName,
      ssh_key_id: sshKeyId || undefined,
      bandwidth_tier: bandwidthTier,
      backup_enabled: backupEnabled,
      monitoring_enabled: monitoringEnabled,
      contract_type: contractType,
      commitment_period: contractType === 'reserved' ? commitmentPeriod : undefined,
      auto_renew: autoRenew,
      disk_layouts: diskLayouts.length > 0 ? diskLayouts : undefined,
      cloud_init_config: cloudInitConfig || undefined,
      technical_contact_name: technicalContactName || undefined,
      technical_contact_email: technicalContactEmail || undefined,
      technical_contact_phone: technicalContactPhone || undefined,
      company_name: companyName || undefined,
    };

    createOrderMutation.mutate(orderRequest);
  };

  const nextStep = () => {
    if (step < 2) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div>
      <Header
        title="Order Bare-Metal Server"
        subtitle="Configure and order a custom bare-metal server"
      />

      <div className="p-6 max-w-4xl mx-auto">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            <div className="flex items-center">
              {/* Step 1 */}
              <div className="flex flex-col items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    step >= 1 ? 'bg-primary-500 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  1
                </div>
                <span className="text-xs mt-2 text-gray-400">Configuration</span>
              </div>

              {/* Connector */}
              <div
                className={`w-24 h-1 mx-2 ${
                  step >= 2 ? 'bg-primary-500' : 'bg-gray-700'
                }`}
              />

              {/* Step 2 */}
              <div className="flex flex-col items-center">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-full ${
                    step >= 2 ? 'bg-primary-500 text-white' : 'bg-gray-700 text-gray-400'
                  }`}
                >
                  2
                </div>
                <span className="text-xs mt-2 text-gray-400">Review</span>
              </div>
            </div>
          </div>
        </div>

        {/* Step 1: Configuration */}
        {step === 1 && (
          <div className="space-y-6">
            {/* CPU */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <CpuChipIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">CPU Cores</h3>
              </div>
              <input
                type="range"
                min="1"
                max="128"
                value={spec.cpu}
                onChange={(e) => setSpec({ ...spec, cpu: Number(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-400 mt-2">
                <span>1 Core</span>
                <span className="text-white font-semibold">{spec.cpu} Cores</span>
                <span>128 Cores</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">₹500 per core per month</p>
            </div>

            {/* RAM */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <CircleStackIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">RAM</h3>
              </div>
              <input
                type="range"
                min="4"
                max="1024"
                step="4"
                value={spec.ram}
                onChange={(e) => setSpec({ ...spec, ram: Number(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-400 mt-2">
                <span>4 GB</span>
                <span className="text-white font-semibold">{spec.ram} GB</span>
                <span>1024 GB</span>
              </div>
              <p className="text-xs text-gray-500 mt-2">₹200 per GB per month</p>
            </div>

            {/* Storage */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <ServerIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">Storage</h3>
              </div>
              <input
                type="range"
                min="100"
                max="10000"
                step="100"
                value={spec.storage}
                onChange={(e) => setSpec({ ...spec, storage: Number(e.target.value) })}
                className="w-full mb-4"
              />
              <div className="flex justify-between text-sm text-gray-400 mb-4">
                <span>100 GB</span>
                <span className="text-white font-semibold">{spec.storage} GB</span>
                <span>10000 GB</span>
              </div>

              <label className="block text-sm font-medium text-gray-300 mb-2">
                Storage Type
              </label>
              <div className="grid grid-cols-3 gap-3">
                {STORAGE_TYPES.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSpec({ ...spec, storage_type: type.value as any })}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      spec.storage_type === type.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">₹50 per GB per month</p>
            </div>

            {/* GPU */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <CpuChipIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">GPU (Optional)</h3>
              </div>

              <label className="block text-sm font-medium text-gray-300 mb-2">
                GPU Type
              </label>
              <select
                value={spec.gpu}
                onChange={(e) => {
                  const value = e.target.value;
                  setSpec({
                    ...spec,
                    gpu: value,
                    gpu_count: value ? 1 : 0,
                  });
                }}
                className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white mb-4"
              >
                {GPU_OPTIONS.map((gpu) => (
                  <option key={gpu.value} value={gpu.value}>
                    {gpu.label}
                  </option>
                ))}
              </select>

              {spec.gpu && (
                <>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    GPU Count
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={spec.gpu_count}
                    onChange={(e) => setSpec({ ...spec, gpu_count: Number(e.target.value) })}
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white"
                  />
                  <p className="text-xs text-gray-500 mt-2">₹10,000 per GPU per month</p>
                </>
              )}
            </div>

            {/* Location */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPinIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">Location</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {LOCATIONS.map((location) => (
                  <button
                    key={location.value}
                    onClick={() => setSpec({ ...spec, location: location.value })}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      spec.location === location.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {location.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Operating System */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <ServerIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">Operating System</h3>
              </div>
              <select
                value={spec.os}
                onChange={(e) => setSpec({ ...spec, os: e.target.value })}
                className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white"
              >
                {OS_OPTIONS.map((os) => (
                  <option key={os.value} value={os.value}>
                    {os.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Duration */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <ClockIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">Duration</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {DURATION_OPTIONS.map((duration) => (
                  <button
                    key={duration.value}
                    onClick={() => setSpec({ ...spec, duration: duration.value })}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      spec.duration === duration.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {duration.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Server Name */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <ServerIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">Server Name *</h3>
              </div>
              <input
                type="text"
                value={serverName}
                onChange={(e) => setServerName(e.target.value)}
                placeholder="e.g., production-ml-server-01"
                className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-500"
                required
              />
              <p className="text-xs text-gray-500 mt-2">A friendly name to identify your server</p>
            </div>

            {/* SSH Key */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <KeyIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">SSH Key (Optional)</h3>
              </div>
              <select
                value={sshKeyId}
                onChange={(e) => setSshKeyId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white"
              >
                <option value="">No SSH Key (use password authentication)</option>
                {sshKeys.map((key: any) => (
                  <option key={key.id} value={key.id}>
                    {key.name} {key.is_default && '(Default)'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Select an SSH key for secure access. You can manage SSH keys in Settings.
              </p>
            </div>

            {/* Network & Services */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <GlobeAltIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">Network & Services</h3>
              </div>

              {/* Bandwidth Tier */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Bandwidth Tier
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'standard', label: 'Standard', desc: '1 Gbps' },
                    { value: 'premium', label: 'Premium', desc: '10 Gbps' },
                    { value: 'enterprise', label: 'Enterprise', desc: '25 Gbps' },
                  ].map((tier) => (
                    <button
                      key={tier.value}
                      onClick={() => setBandwidthTier(tier.value as any)}
                      className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        bandwidthTier === tier.value
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }`}
                    >
                      <div>{tier.label}</div>
                      <div className="text-xs opacity-75">{tier.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Backup & Monitoring */}
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors">
                  <input
                    type="checkbox"
                    checked={backupEnabled}
                    onChange={(e) => setBackupEnabled(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 text-primary-500 focus:ring-primary-500 bg-gray-800"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-white">Enable Daily Backups</span>
                    <p className="text-xs text-gray-400">Automated daily backups with 7-day retention (+₹500/month)</p>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors">
                  <input
                    type="checkbox"
                    checked={monitoringEnabled}
                    onChange={(e) => setMonitoringEnabled(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 text-primary-500 focus:ring-primary-500 bg-gray-800"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-white">Enable Monitoring</span>
                    <p className="text-xs text-gray-400">24/7 server monitoring with alerts (+₹300/month)</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <UserIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">Contact Information (Optional)</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Technical Contact Name
                  </label>
                  <input
                    type="text"
                    value={technicalContactName}
                    onChange={(e) => setTechnicalContactName(e.target.value)}
                    placeholder="John Doe"
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Technical Contact Email
                  </label>
                  <input
                    type="email"
                    value={technicalContactEmail}
                    onChange={(e) => setTechnicalContactEmail(e.target.value)}
                    placeholder="john@company.com"
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Technical Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={technicalContactPhone}
                    onChange={(e) => setTechnicalContactPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Acme Corporation"
                    className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                This information helps our support team contact you during provisioning
              </p>
            </div>

            {/* Contract Type & Commitment */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center gap-3 mb-4">
                <ClockIcon className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-semibold text-white">Contract Type</h3>
              </div>

              {/* Contract Type Selection */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <button
                  onClick={() => {
                    setContractType('on_demand');
                    setCommitmentPeriod(0);
                  }}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    contractType === 'on_demand'
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                  }`}
                >
                  <div className="text-left">
                    <p className="font-medium text-white">On-Demand</p>
                    <p className="text-xs text-gray-400 mt-1">Pay as you go, cancel anytime</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setContractType('reserved');
                    if (commitmentPeriod === 0) setCommitmentPeriod(3);
                  }}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    contractType === 'reserved'
                      ? 'border-primary-500 bg-primary-500/10'
                      : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                  }`}
                >
                  <div className="text-left">
                    <p className="font-medium text-white">Reserved</p>
                    <p className="text-xs text-gray-400 mt-1">Commit for savings, up to 20% off</p>
                  </div>
                </button>
              </div>

              {/* Commitment Period (for Reserved) */}
              {contractType === 'reserved' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Commitment Period
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { months: 3, discount: 10 },
                      { months: 6, discount: 15 },
                      { months: 12, discount: 20 },
                    ].map((option) => (
                      <button
                        key={option.months}
                        onClick={() => setCommitmentPeriod(option.months)}
                        className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                          commitmentPeriod === option.months
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        <div>{option.months} Months</div>
                        <div className="text-xs opacity-75">Save {option.discount}%</div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Longer commitments offer better pricing
                  </p>
                </div>
              )}

              {/* Auto-Renew */}
              <div className="mt-4">
                <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors">
                  <input
                    type="checkbox"
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 text-primary-500 focus:ring-primary-500 bg-gray-800"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-white">Auto-renew reservation</span>
                    <p className="text-xs text-gray-400">Automatically renew at the end of the period</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Advanced Configuration Toggle */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-3">
                  <AdjustmentsHorizontalIcon className="w-6 h-6 text-primary-500" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">Advanced Configuration</h3>
                    <p className="text-xs text-gray-400">Disk layouts and cloud-init</p>
                  </div>
                </div>
                <ChevronRightIcon
                  className={`w-5 h-5 text-gray-400 transition-transform ${
                    showAdvanced ? 'rotate-90' : ''
                  }`}
                />
              </button>

              {showAdvanced && (
                <div className="mt-6 space-y-6">
                  {/* Disk Layouts */}
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-3">Disk Layouts</h4>
                    {diskLayouts.map((layout, index) => (
                      <div key={index} className="bg-gray-800 rounded-lg p-4 mb-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-400 mb-1">
                              Disk Type
                            </label>
                            <select
                              value={layout.disk_type}
                              onChange={(e) => {
                                const updated = [...diskLayouts];
                                updated[index].disk_type = e.target.value;
                                setDiskLayouts(updated);
                              }}
                              className="w-full px-3 py-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white"
                            >
                              <option value="NVME">NVMe</option>
                              <option value="SSD">SSD</option>
                              <option value="HDD">HDD</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-400 mb-1">
                              Disk Count
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={layout.disk_count}
                              onChange={(e) => {
                                const updated = [...diskLayouts];
                                updated[index].disk_count = Number(e.target.value);
                                setDiskLayouts(updated);
                              }}
                              className="w-full px-3 py-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-400 mb-1">
                              Layout
                            </label>
                            <select
                              value={layout.layout}
                              onChange={(e) => {
                                const updated = [...diskLayouts];
                                updated[index].layout = e.target.value;
                                setDiskLayouts(updated);
                              }}
                              className="w-full px-3 py-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white"
                            >
                              <option value="lvm">LVM</option>
                              <option value="raid0">RAID 0</option>
                              <option value="raid1">RAID 1</option>
                              <option value="single">Single</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-400 mb-1">
                              Format
                            </label>
                            <select
                              value={layout.format}
                              onChange={(e) => {
                                const updated = [...diskLayouts];
                                updated[index].format = e.target.value;
                                setDiskLayouts(updated);
                              }}
                              className="w-full px-3 py-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white"
                            >
                              <option value="ext4">ext4</option>
                              <option value="xfs">xfs</option>
                              <option value="btrfs">btrfs</option>
                            </select>
                          </div>
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-400 mb-1">
                              Mount Point
                            </label>
                            <input
                              type="text"
                              value={layout.mountpoint}
                              onChange={(e) => {
                                const updated = [...diskLayouts];
                                updated[index].mountpoint = e.target.value;
                                setDiskLayouts(updated);
                              }}
                              placeholder="/data0"
                              className="w-full px-3 py-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white"
                            />
                          </div>
                        </div>
                        {diskLayouts.length > 1 && (
                          <button
                            onClick={() => {
                              setDiskLayouts(diskLayouts.filter((_, i) => i !== index));
                            }}
                            className="mt-2 text-xs text-red-400 hover:text-red-300"
                          >
                            Remove Layout
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        setDiskLayouts([
                          ...diskLayouts,
                          {
                            disk_type: 'NVME',
                            disk_count: 1,
                            size_per_disk_gb: 500,
                            layout: 'lvm',
                            format: 'ext4',
                            mountpoint: `/data${diskLayouts.length}`,
                          },
                        ]);
                      }}
                      className="text-sm text-primary-500 hover:text-primary-400 font-medium"
                    >
                      + Add Disk Layout
                    </button>
                  </div>

                  {/* Cloud-Init */}
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-2">Cloud-Init Configuration</h4>
                    <p className="text-xs text-gray-400 mb-3">
                      Enter your cloud-init YAML configuration for automated setup
                    </p>
                    <textarea
                      value={cloudInitConfig}
                      onChange={(e) => setCloudInitConfig(e.target.value)}
                      rows={8}
                      placeholder="#cloud-config&#10;packages:&#10;  - nginx&#10;  - docker.io&#10;&#10;runcmd:&#10;  - systemctl start docker"
                      className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-700 text-white placeholder-gray-500 font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Properties like disk_setup, fs_setup, and network-config are not supported
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Additional Notes (Optional)
              </h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Any special requirements or notes for the support team..."
                className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-gray-800 text-white placeholder-gray-500"
              />
            </div>

            {/* Pricing Summary */}
            <div className="bg-gradient-to-r from-primary-500/10 to-primary-600/10 border border-primary-500/20 rounded-xl p-6">
              {isPricingLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  <span className="ml-3 text-gray-400">Calculating pricing...</span>
                </div>
              ) : (
                <>
                  {pricing.discount_percentage > 0 && (
                    <>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-400 line-through">Base Monthly Price:</span>
                        <span className="text-lg text-gray-400 line-through">
                          ₹{pricing.monthly_price_inr.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-green-400 font-medium">
                          After {pricing.discount_percentage}% Discount:
                        </span>
                        <span className="text-2xl font-bold text-green-400">
                          ₹{pricing.discounted_monthly_price_inr.toLocaleString()}/mo
                        </span>
                      </div>
                    </>
                  )}
                  {pricing.discount_percentage === 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300">Monthly Price:</span>
                      <span className="text-2xl font-bold text-white">
                        ₹{pricing.monthly_price_inr.toLocaleString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center border-t border-gray-700 pt-3">
                    <span className="text-gray-300">
                      Total Price ({contractType === 'reserved' ? commitmentPeriod : spec.duration} month{(contractType === 'reserved' ? commitmentPeriod : spec.duration) > 1 ? 's' : ''}):
                    </span>
                    <span className="text-3xl font-bold text-primary-400">
                      ₹{pricing.total_price_inr.toLocaleString()}
                    </span>
                  </div>
                  {pricing.discount_percentage > 0 && (
                    <p className="text-xs text-green-400 mt-2 text-right">
                      You save ₹{((pricing.monthly_price_inr - pricing.discounted_monthly_price_inr) * (contractType === 'reserved' ? commitmentPeriod : spec.duration)).toLocaleString()} with {commitmentPeriod}-month commitment!
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Navigation */}
            <div className="flex justify-end">
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg text-white font-medium transition-colors"
              >
                Continue to Review
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <h3 className="text-xl font-semibold text-white mb-6">Order Summary</h3>

              <div className="space-y-4">
                <div className="flex justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-400">CPU:</span>
                  <span className="text-white font-medium">{spec.cpu} Cores</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-400">RAM:</span>
                  <span className="text-white font-medium">{spec.ram} GB</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-400">Storage:</span>
                  <span className="text-white font-medium">
                    {spec.storage} GB {spec.storage_type}
                  </span>
                </div>
                {spec.gpu && (
                  <div className="flex justify-between py-3 border-b border-gray-700">
                    <span className="text-gray-400">GPU:</span>
                    <span className="text-white font-medium">
                      {spec.gpu_count}x {spec.gpu}
                    </span>
                  </div>
                )}
                <div className="flex justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-400">Location:</span>
                  <span className="text-white font-medium">{spec.location}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-400">Operating System:</span>
                  <span className="text-white font-medium">{spec.os}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white font-medium">{spec.duration} month{spec.duration > 1 ? 's' : ''}</span>
                </div>
                <div className="flex justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-400">Server Name:</span>
                  <span className="text-white font-medium">{serverName}</span>
                </div>
                {sshKeyId && (
                  <div className="flex justify-between py-3 border-b border-gray-700">
                    <span className="text-gray-400">SSH Key:</span>
                    <span className="text-white font-medium">
                      {sshKeys.find((k: any) => k.id === sshKeyId)?.name || 'Selected'}
                    </span>
                  </div>
                )}
                <div className="flex justify-between py-3 border-b border-gray-700">
                  <span className="text-gray-400">Bandwidth:</span>
                  <span className="text-white font-medium capitalize">{bandwidthTier}</span>
                </div>
                {(backupEnabled || monitoringEnabled) && (
                  <div className="py-3 border-b border-gray-700">
                    <span className="text-gray-400 block mb-2">Additional Services:</span>
                    <div className="space-y-1">
                      {backupEnabled && <p className="text-white text-sm">✓ Daily Backups (+₹500/month)</p>}
                      {monitoringEnabled && <p className="text-white text-sm">✓ 24/7 Monitoring (+₹300/month)</p>}
                    </div>
                  </div>
                )}
                {(technicalContactName || technicalContactEmail || companyName) && (
                  <div className="py-3 border-b border-gray-700">
                    <span className="text-gray-400 block mb-2">Contact Information:</span>
                    <div className="space-y-1 text-sm">
                      {technicalContactName && <p className="text-white">Name: {technicalContactName}</p>}
                      {technicalContactEmail && <p className="text-white">Email: {technicalContactEmail}</p>}
                      {technicalContactPhone && <p className="text-white">Phone: {technicalContactPhone}</p>}
                      {companyName && <p className="text-white">Company: {companyName}</p>}
                    </div>
                  </div>
                )}
                <div className="py-3 border-b border-gray-700">
                  <span className="text-gray-400 block mb-2">Contract Type:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium capitalize">{contractType.replace('_', ' ')}</span>
                    {contractType === 'reserved' && (
                      <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs font-medium">
                        {commitmentPeriod} months - Save {pricing.discount_percentage}%
                      </span>
                    )}
                  </div>
                  {autoRenew && (
                    <p className="text-sm text-blue-400 mt-1">✓ Auto-renew enabled</p>
                  )}
                </div>
                {diskLayouts.length > 0 && (
                  <div className="py-3 border-b border-gray-700">
                    <span className="text-gray-400 block mb-2">Disk Layouts:</span>
                    <div className="space-y-2">
                      {diskLayouts.map((layout, index) => (
                        <div key={index} className="text-sm bg-gray-800 rounded p-2">
                          <p className="text-white">
                            <span className="font-medium">{layout.disk_type}</span> - {layout.disk_count}x disk(s)
                          </p>
                          <p className="text-gray-400 text-xs">
                            {layout.layout} / {layout.format} / {layout.mountpoint}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {cloudInitConfig && (
                  <div className="py-3 border-b border-gray-700">
                    <span className="text-gray-400 block mb-2">Cloud-Init:</span>
                    <pre className="text-white text-xs bg-gray-800 p-3 rounded overflow-x-auto font-mono">
                      {cloudInitConfig.substring(0, 200)}{cloudInitConfig.length > 200 && '...'}
                    </pre>
                  </div>
                )}
                {notes && (
                  <div className="py-3 border-b border-gray-700">
                    <span className="text-gray-400 block mb-2">Notes:</span>
                    <span className="text-white text-sm">{notes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Pricing Breakdown */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <h3 className="text-xl font-semibold text-white mb-6">Pricing Breakdown</h3>

              <div className="space-y-3">
                <div className="flex justify-between text-gray-400">
                  <span>CPU ({spec.cpu} cores × ₹500):</span>
                  <span>₹{(spec.cpu * 500).toLocaleString()}/month</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>RAM ({spec.ram} GB × ₹200):</span>
                  <span>₹{(spec.ram * 200).toLocaleString()}/month</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Storage ({spec.storage} GB × ₹50):</span>
                  <span>₹{(spec.storage * 50).toLocaleString()}/month</span>
                </div>
                {spec.gpu && spec.gpu_count && spec.gpu_count > 0 && (
                  <div className="flex justify-between text-gray-400">
                    <span>GPU ({spec.gpu_count}x × ₹10,000):</span>
                    <span>₹{(spec.gpu_count * 10000).toLocaleString()}/month</span>
                  </div>
                )}
                {backupEnabled && (
                  <div className="flex justify-between text-gray-400">
                    <span>Daily Backups:</span>
                    <span>₹500/month</span>
                  </div>
                )}
                {monitoringEnabled && (
                  <div className="flex justify-between text-gray-400">
                    <span>24/7 Monitoring:</span>
                    <span>₹300/month</span>
                  </div>
                )}
                <div className="border-t border-gray-700 pt-3 mt-3">
                  <div className="flex justify-between text-white font-semibold text-lg">
                    <span>Monthly Price:</span>
                    <span>₹{pricing.monthly_price_inr.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-primary-400 font-bold text-2xl mt-2">
                    <span>Total Price ({spec.duration} month{spec.duration > 1 ? 's' : ''}):</span>
                    <span>₹{pricing.total_price_inr.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-600 text-primary-500 focus:ring-primary-500 bg-gray-800"
                />
                <span className="text-sm text-gray-300">
                  I accept the terms and conditions. I understand that this order will be manually provisioned by the support team, and I will receive server details via email once the server is ready.
                </span>
              </label>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                onClick={prevStep}
                className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-medium transition-colors"
              >
                <ChevronLeftIcon className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={!termsAccepted || createOrderMutation.isPending}
                className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
              >
                {createOrderMutation.isPending ? 'Creating Order...' : 'Proceed to Checkout'}
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateOrder;
